import React, { useState, useEffect, useRef } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';
import html2canvas from 'html2canvas';

const AdminStudentCardManage = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // State for student cards data
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterCardType, setFilterCardType] = useState('library');
  const [filterStatus, setFilterStatus] = useState('');
  const [limit] = useState(10);

  // Modal states

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  // Editable certificate fields state
  const [certificateData, setCertificateData] = useState({
    serialNo: 'REG/2023/3378',
    studentName: '',
    parentName: '',
    regNumber: '',
    regYear: ''
  });
  const [editingField, setEditingField] = useState(null);

  // Barcode Ref
  const canvasRef = useRef([]);

  // DYNAMIC DEPARTMENT AND BATCH DATA (NEW)
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Form state - UPDATED FOR HIERARCHICAL SELECTION
  const [formData, setFormData] = useState({
    department: '',
    year: 2001, // Start with 2001 (1st year in database)
    batch_name: '',
    student_id: '',
    card_type: 'library',
    card_name: '',
    expiry_date: '',
    remarks: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // SEARCH STUDENTS STATE
  const [allStudents, setAllStudents] = useState([]);
  const [matchedStudents, setMatchedStudents] = useState([]);
  const [isSearchingStudent, setIsSearchingStudent] = useState(false);

  // Card types configuration
  const cardTypes = [
    { value: 'registration', label: '📝 Registration Card', color: '#6c757d' },
    { value: 'library', label: '📚 Library Card', color: '#28a745' },
    { value: 'id_card', label: '🆔 Student ID Card', color: '#007bff' },
    { value: 'laundry_card', label: '🧺 Laundry Card', color: '#6f42c1' },
    { value: 'mess_card', label: '🍽️ Mess Card', color: '#17a2b8' },
    { value: 'admit', label: '🎓 Admit Card', color: '#fd7e14' }
  ]


  // Year options
  const yearOptions = [
    { value: 1, label: '1st Year' },
    { value: 2, label: '2nd Year' },
    { value: 3, label: '3rd Year' },
    { value: 4, label: '4th Year' }
  ];

  // Helper to format date DD-MM-YYYY
  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper to generate a unique 16-digit library card number (XXXX XXXX XXXX XXXX)
  const generateLibraryCardNumber = () => {
    return Array.from({ length: 4 }, () =>
      Math.floor(1000 + Math.random() * 9000)
    ).join('  '); // Double space for embossed look
  };

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  // API HELPER FUNCTION
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // FETCH DEPARTMENTS FROM DATABASE
  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const result = await apiCall('/departments');

      if (result.success) {
        setDepartments(result.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError('Failed to fetch departments: ' + error.message);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // FETCH BATCHES FROM DATABASE (FOR FORMS)
  const fetchBatches = async (department, year) => {
    if (!department || !year) {
      setBatches([]);
      return;
    }

    try {
      setBatchesLoading(true);
      console.log('Fetching batches for form:', { department, year });
      const result = await apiCall(`/batches?department=${encodeURIComponent(department)}&year=${year}`);

      if (result.success && result.data) {
        console.log('Form batches loaded:', result.data);
        setBatches(result.data);
      } else {
        console.log('No batches found for form');
        setBatches([]);
      }
    } catch (error) {
      console.error('Error fetching form batches:', error);
      setBatches([]);
      setError('Failed to fetch batches: ' + error.message);
    } finally {
      setBatchesLoading(false);
    }
  };

  // FETCH STUDENTS BASED ON DEPARTMENT, YEAR, AND BATCH - FIXED VERSION
  const fetchStudents = async (department, year, batch) => {
    if (!department || !year || !batch) {
      setStudents([]);
      return;
    }

    try {
      setStudentsLoading(true);

      // Convert database year format (2001, 2002, 2003, 2004) to UI format (1, 2, 3, 4)
      const uiYear = year - 2000;

      console.log('Fetching students:', { department, year: uiYear, batch });
      const result = await apiCall(`/students/filter?department=${encodeURIComponent(department)}&year=${uiYear}&batch=${encodeURIComponent(batch)}`);

      if (result.success && result.data) {
        console.log('Students loaded:', result.data);
        setStudents(result.data.students || []);
      } else {
        console.log('No students found');
        setStudents([]);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
      // Don't set global error for student fetch failures - handle locally
      console.warn('Failed to fetch students - this might be because the endpoint is not available or no students exist for this combination');
    } finally {
      setStudentsLoading(false);
    }
  };

  // FETCH INITIAL DATA
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Fetch initial data on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInitialData();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when filters change (using debounced search term)
  useEffect(() => {
    // Prevent fetching if loading is true initially to avoid double fetch, 
    // but allow subsequent fetches when filters change
    if (!loading) {
      fetchCards(1, debouncedSearchTerm, filterCardType, filterStatus, false);
    }
  }, [debouncedSearchTerm, filterCardType, filterStatus]);



  // Fetch initial data
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch cards
      await fetchCards(1, '', '', '');

    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load initial data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // FETCH ALL STUDENTS FOR SEARCH
  const fetchAllStudents = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      // Fetch a large number of students for client-side search
      const response = await fetch(`${API_LINK}/api/admin/students?page=1&limit=50000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setAllStudents(result.data?.students || []);
      }
    } catch (error) {
      console.error('Error fetching all students:', error);
    }
  };

  // Load all students on mount
  useEffect(() => {
    fetchAllStudents();
  }, []);

  // FILTER STATES (ADDED)
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterSemester, setFilterSemester] = useState('');
  const [filterBatch, setFilterBatch] = useState('');

  // DERIVED OPTIONS FROM ALL STUDENTS
  const uniqueDepartments = [...new Set(allStudents.map(s => s.dept).filter(Boolean))].sort();

  // Dependent Batch Options (Only show batches for selected department)
  const availableBatches = filterDepartment
    ? allStudents.filter(s => s.dept === filterDepartment)
    : allStudents;

  const uniqueBatches = [...new Set(availableBatches.map(s => s.batch_name).filter(Boolean))].sort();
  const semesterOptions = [1, 2, 3, 4, 5, 6, 7, 8];

  // SELECTION STATE (ADDED)
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [previewStudent, setPreviewStudent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [examSubjects, setExamSubjects] = useState([]); // State for Admit Card subjects
  const [admitCardNo, setAdmitCardNo] = useState(''); // Unique admit card number
  const admitCardRef = useRef(null); // Ref for admit card capture

  // Toggle selection for all filtered students
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all currently visible students
      setSelectedStudents(matchedStudents.map(s => s.id || s.student_id));
    } else {
      setSelectedStudents([]);
    }
  };

  // Toggle individual selection
  const handleSelectStudent = (id) => {
    if (selectedStudents.includes(id)) {
      setSelectedStudents(prev => prev.filter(sid => sid !== id));
    } else {
      setSelectedStudents(prev => [...prev, id]);
    }
  };

  // Fetch exam subjects for admit card
  const fetchExamSubjects = async (student) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const params = new URLSearchParams();
      if (student.dept) params.append('department', student.dept);
      if (student.year) params.append('year', String(student.year));
      if (student.batch_name) params.append('batch', student.batch_name);

      console.log('Fetching exam subjects with params:', params.toString());

      const response = await fetch(`${API_LINK}/api/admin/exam-schedule?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      console.log('Exam subjects result:', result);
      if (result.success) {
        setExamSubjects(result.data || []);
      } else {
        setExamSubjects([]);
      }
    } catch (err) {
      console.error('Error fetching exam subjects:', err);
      setExamSubjects([]);
    }
  };

  // Action Handlers
  const handleViewCard = (student) => {
    // Generate a temporary card number for preview ONLY if not already assigned and not already generated
    if (filterCardType === 'library' && !student.card_number && !student.temp_card_number) {
      student.temp_card_number = generateLibraryCardNumber();
    }
    // Reset photo loaded state for new preview
    const hasPhoto = student.image_path || student.photo_path || student.photo_url || student.photo || student.image || student.profile_image || student.profileImage;
    setPhotoLoaded(!hasPhoto); // If no photo, consider it already "loaded"
    setPreviewStudent(student);
    setShowPreview(true);
    // Fetch exam subjects and admit card number when viewing an admit card
    if (filterCardType === 'admit') {
      fetchExamSubjects(student);
      fetchAdmitCardNo(student);
    }
  };

  // Fetch admit card number for a student
  const fetchAdmitCardNo = async (student) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const sid = student.student_id || student.id;
      const response = await fetch(`${API_LINK}/api/admin/admit-card-number/${sid}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setAdmitCardNo(result.card_number);
      } else {
        setAdmitCardNo('');
      }
    } catch (err) {
      console.error('Error fetching admit card number:', err);
      setAdmitCardNo('');
    }
  };

  // Download & Assign Admit Card - captures the card as image and uploads to server
  const handleDownloadAndAssignAdmitCard = async () => {
    if (!admitCardRef.current || !previewStudent) return;
    try {
      const canvas = await html2canvas(admitCardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f5f5f0'
      });

      // Convert to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

      // Also trigger download for admin
      const link = document.createElement('a');
      link.download = `Admit_Card_${previewStudent.student_id || previewStudent.id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      // Upload to server via assign-admit-card endpoint
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', blob, `Admit_Card_${previewStudent.student_id || previewStudent.id}.png`);
      formData.append('student_id', previewStudent.student_id || previewStudent.id);
      formData.append('exam_session', 'Current');

      const response = await fetch(`${API_LINK}/api/admin/assign-admit-card`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const result = await response.json();
      if (result.success) {
        alert('Admit card downloaded and assigned to student successfully!');
        if (result.card_number) setAdmitCardNo(result.card_number);
      } else {
        alert('Card downloaded locally but failed to assign to student: ' + result.message);
      }
    } catch (err) {
      console.error('Error downloading/assigning admit card:', err);
      alert('Error: ' + err.message);
    }
  };

  const handleGenerateCard = (student) => {
    console.log('Generate card for:', student);
    alert(`Generating card for ${student.name}`);
    // Implement generation logic
  };

  const handleAutoAssignCard = async (student) => {
    try {
      if (!window.html2canvas) {
        alert('Library for generating card not loaded. Please refresh the page.');
        return;
      }

      // Determine endpoint and label based on card type
      let endpoint = '';
      let confirmLabel = '';

      if (filterCardType === 'registration') {
        endpoint = 'assign-registration-card';
        confirmLabel = 'Registration Card';
      } else if (filterCardType === 'id_card') {
        endpoint = 'assign-student-card';
        confirmLabel = 'Student ID Card';
      } else if (filterCardType === 'admit') {
        endpoint = 'assign-admit-card';
        confirmLabel = 'Admit Card';
      } else {
        endpoint = 'assign-library-card';
        confirmLabel = 'Library Card';
      }

      // Clone the element to ensure full capture without scrolling issues
      const originalElement = document.getElementById('card-preview-target');
      if (!originalElement) {
        alert('Card preview not found');
        return;
      }

      const confirmAssign = window.confirm(`Assign ${confirmLabel} to ${student.name || student.student_name}?`);
      if (!confirmAssign) return;

      // --- NEW PDF GENERATION LOGIC ---
      // We use a temporary container that is part of the document flow (absolute) 
      // but sits on top of everything. We make it large enough to hold the card without scrolling.

      // 1. Create a temporary container
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '2000px'; // Force large width
      container.style.height = '2000px'; // Force large height
      container.style.backgroundColor = '#ffffff';
      container.style.zIndex = '999999'; // On top of everything
      container.style.overflow = 'visible'; // No clipping
      document.body.appendChild(container);

      // 2. Clone the element
      const element = originalElement.cloneNode(true);

      // 3. Reset styles on the clone
      element.style.position = 'absolute';
      element.style.top = '50px'; // Slight offset
      element.style.left = '50px';
      element.style.transform = 'none';
      element.style.margin = '0';
      element.style.boxShadow = 'none';

      // 4. Append clone to container
      container.appendChild(element);

      // 5. Force scroll to top-left to ensure the canvas starts at 0,0
      window.scrollTo(0, 0);

      // 6. Brief wait for DOM to settle (images, fonts)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 7. Capture
      // We capture the specific CLONED element, not the container
      const canvas = await window.html2canvas(element, {
        scale: 2, // High res
        useCORS: true,
        logging: true,
        backgroundColor: '#ffffff',
        allowTaint: true,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0
      });

      // 8. Cleanup
      document.body.removeChild(container);

      // --- END NEW PDF GENERATION LOGIC ---

      // Convert canvas to PDF using jsPDF
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Card-sized PDF (convert pixels to mm at 96 DPI: px * 25.4 / 96 / scale)
      const pdfWidth = imgWidth * 25.4 / (96 * 2);
      const pdfHeight = imgHeight * 25.4 / (96 * 2);

      let pdf;
      if (window.jspdf && window.jspdf.jsPDF) {
        pdf = new window.jspdf.jsPDF({
          orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight]
        });
      } else {
        alert('PDF library not loaded. Please refresh the page.');
        return;
      }

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      const pdfBlob = pdf.output('blob');

      const formData = new FormData();
      formData.append('file', pdfBlob, `card_${student.student_id}.pdf`);
      formData.append('student_id', student.student_id);

      // Append specific data based on card type
      if (filterCardType === 'registration') {
        const yearValue = certificateData.regYear;
        formData.append('year', yearValue);
        formData.append('card_number', student.student_id);
      } else if (filterCardType === 'admit') {
        formData.append('exam_session', `Sem ${student.semester || ''} - ${new Date().getFullYear()}`);
      } else if (filterCardType === 'library') {
        // Use the already-generated temp_card_number (from preview) or existing card_number
        const libCardNo = student.card_number || student.temp_card_number || generateLibraryCardNumber();
        formData.append('card_number', libCardNo);
      } else {
        formData.append('card_number', student.student_id);
      }

      try {
        const token = localStorage.getItem('token');

        const response = await fetch(`${API_LINK}/api/admin/${endpoint}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const result = await response.json();
        if (result.success) {
          alert('Card assigned successfully!');
          setShowPreview(false);
          fetchCards(currentPage, debouncedSearchTerm, filterCardType, filterStatus, false);
        } else {
          alert('Failed to assign card: ' + result.message);
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert('Error uploading card assignment');
      }

    } catch (err) {
      console.error(err);
      alert('Error generating card assignment');
    }
  };

  const handleBulkGenerate = () => {
    if (selectedStudents.length === 0) return;
    console.log('Generating cards for:', selectedStudents);
    alert(`Generating ${selectedStudents.length} cards`);
  };

  // INITIALIZE BARCODES
  useEffect(() => {
    try {
      if (window.JsBarcode) {
        window.JsBarcode(".barcode").init();
      }
    } catch (error) {
      console.error("Barcode initialization error:", error);
    }
  }, [cards, previewStudent, showPreview, filterCardType, currentPage]);

  // Filter students when search term or filters change
  useEffect(() => {
    // If no search term and no filters, don't show result list
    // UNLESS card type specific logic needed, but for student search:
    if ((!searchTerm || !searchTerm.trim()) && !filterDepartment && !filterYear && !filterSemester && !filterBatch) {
      setMatchedStudents([]);
      setIsSearchingStudent(false);
      return;
    }

    let result = allStudents;

    // 1. Text Search
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(s =>
        (s.name && s.name.toLowerCase().includes(term)) ||
        (s.student_id && s.student_id.toLowerCase().includes(term))
      );
    }

    // 2. Department Filter
    if (filterDepartment) {
      result = result.filter(s => s.dept === filterDepartment);
    }

    // 3. Batches Filter
    if (filterBatch) {
      result = result.filter(s => s.batch_name === filterBatch);
    }

    // 4. Year Filter
    if (filterYear) {
      // DB stores year as 2001, 2002...
      // User selects 1, 2, 3, 4
      result = result.filter(s => s.year && (s.year - 2000) === parseInt(filterYear));
    }

    // 5. Semester Filter
    if (filterSemester) {
      // Map semester 1-8 to year 1-4. (Sem 1,2 -> Year 1; Sem 3,4 -> Year 2...)
      // Logic: Year = ceil(Sem / 2)
      const targetYearOffset = Math.ceil(parseInt(filterSemester) / 2); // 1, 2, 3, or 4

      result = result.filter(s => {
        // Direct match if semester field exists and is just a number 1-8
        if (s.semester == filterSemester) return true;

        // Derived match from Year
        if (s.year && (s.year - 2000) === targetYearOffset) return true;

        return false;
      });
    }

    setMatchedStudents(result);
    setIsSearchingStudent(true); // Always true if filtering is active so UI shows

  }, [searchTerm, allStudents, filterDepartment, filterBatch, filterYear, filterSemester]);

  // Fetch student cards from API
  const fetchCards = async (page = 1, search = '', cardType = '', status = '', showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && search.trim() && { search: search.trim() }),
        ...(cardType && cardType.trim() && { card_type: cardType.trim() }),
        ...(status && status.trim() && { status: status.trim() })
      });

      const apiUrl = `${API_LINK}/api/admin/student-cards?${params}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Student cards API endpoint not found. Please check if the backend server is running correctly.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred. Please check the backend logs.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setError('Server returned invalid response format. Expected JSON.');
        return;
      }

      const result = await response.json();

      if (result.success) {
        const cardsData = result.data?.cards || [];
        const paginationData = result.data?.pagination || {};

        setCards(cardsData);
        setCurrentPage(paginationData.currentPage || page);
        setTotalPages(paginationData.totalPages || 1);
        setTotalCount(paginationData.totalCount || cardsData.length);
      } else {
        setError(result.message || 'Failed to fetch student cards from server');
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
      } else if (error.message.includes('JSON')) {
        setError('Server returned invalid data format. Please check backend API.');
      } else {
        setError(`Failed to load student cards: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // FORM HANDLERS FOR HIERARCHICAL SELECTION
  const handleFormDepartmentChange = (value) => {
    console.log('Form department changed to:', value);
    setFormData({ ...formData, department: value, batch_name: '', student_id: '' });
    setBatches([]);
    setStudents([]);

    if (value && formData.year) {
      console.log('Fetching batches for dept:', value, 'year:', formData.year);
      fetchBatches(value, formData.year);
    }
  };

  const handleFormYearChange = (value) => {
    console.log('Form year changed to:', value);
    // Value is already the database year format (2001, 2002, etc.)
    const dbYear = parseInt(value);
    setFormData({ ...formData, year: dbYear, batch_name: '', student_id: '' });
    setBatches([]);
    setStudents([]);

    if (dbYear && formData.department) {
      console.log('Fetching batches for dept:', formData.department, 'year:', dbYear);
      fetchBatches(formData.department, dbYear);
    }
  };

  const handleFormBatchChange = (value) => {
    console.log('Form batch changed to:', value);
    setFormData({ ...formData, batch_name: value, student_id: '' });
    setStudents([]);

    if (value && formData.department && formData.year) {
      console.log('Fetching students for:', {
        department: formData.department,
        year: formData.year,
        batch: value
      });
      fetchStudents(formData.department, formData.year, value);
    }
  };

  // Handle search and filters
  const handleSearch = () => {
    setCurrentPage(1);
    fetchCards(1, searchTerm, filterCardType, filterStatus);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page && page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchCards(page, searchTerm, filterCardType, filterStatus);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file only');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should not exceed 10MB');
        return;
      }

      setSelectedFile(file);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.department.trim()) errors.department = 'Department is required';
    if (!formData.batch_name.trim()) errors.batch_name = 'Batch is required';
    if (!formData.student_id.trim()) errors.student_id = 'Student is required';
    if (!formData.card_type.trim()) errors.card_type = 'Card type is required';
    if (!formData.card_name.trim()) errors.card_name = 'Card name is required';
    if (!selectedFile) errors.file = 'PDF file is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle assign card
  const handleAssignCard = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const formDataToSend = new FormData();
      formDataToSend.append('student_id', formData.student_id);
      formDataToSend.append('card_type', formData.card_type);
      formDataToSend.append('card_name', formData.card_name);
      formDataToSend.append('expiry_date', formData.expiry_date || '');
      formDataToSend.append('remarks', formData.remarks);
      formDataToSend.append('card_file', selectedFile);

      const response = await fetch(`${API_LINK}/api/admin/student-cards/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('Card assigned successfully!');

        // Refresh cards list
        await fetchCards(currentPage, searchTerm, filterCardType, filterStatus);
      } else {
        alert('Error: ' + (result.message || 'Failed to assign card'));
      }
    } catch (error) {
      console.error('Error assigning card:', error);
      alert('Failed to assign card: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete card
  const handleDeleteCard = async () => {
    if (!selectedCard) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/admin/student-cards/${selectedCard.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('Card deleted successfully!');
        await fetchCards(currentPage, searchTerm, filterCardType, filterStatus);
        setShowDeleteModal(false);
        setSelectedCard(null);
      } else {
        alert('Error: ' + (result.message || 'Failed to delete card'));
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      alert('Failed to delete card: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle status update
  const handleUpdateCardStatus = async (cardId, newStatus) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/admin/student-cards/${cardId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('Card status updated successfully!');
        await fetchCards(currentPage, searchTerm, filterCardType, filterStatus);
      } else {
        alert('Error: ' + (result.message || 'Failed to update status'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  };



  // Handle delete click
  const handleDeleteClick = (card) => {
    setSelectedCard(card);
    setShowDeleteModal(true);
  };



  // Get card type info - FIXED VERSION
  const getCardTypeInfo = (type) => {
    if (!type || typeof type !== 'string') {
      return cardTypes[cardTypes.length - 1]; // Return 'other' as default
    }
    return cardTypes.find(ct => ct.value === type) || cardTypes[cardTypes.length - 1];
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Editable field with hover edit icon
  const EditableField = ({ children, style = {}, isBlock = false, ...props }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
      <span
        contentEditable
        suppressContentEditableWarning
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: 'relative',
          display: isBlock ? 'block' : 'inline',
          outline: 'none',
          cursor: 'text',
          transition: 'background 0.2s',
          background: isHovered ? '#f0fff4' : 'transparent',
          borderRadius: '3px',
          padding: '2px 4px',
          ...style
        }}
        {...props}
      >
        {children}
        {isHovered && (
          <span style={{
            position: 'absolute',
            right: '-18px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '0.75rem',
            opacity: 0.7,
            pointerEvents: 'none'
          }}>✏️</span>
        )}
      </span>
    );
  };

  // RENDER CARD TEMPLATE FUNCTION from Modal
  const renderCardTemplate = (type, student = null, index = 0) => {
    const s = student || {}; // Short alias

    switch (type) {
      case 'registration':
        return (
          <div style={{
            width: '900px',
            aspectRatio: '3/2',
            background: '#fff',
            padding: '8px',
            boxShadow: '0 0 20px rgba(0,0,0,0.15)',
            border: '3px solid #18664d',
            position: 'relative',
            fontFamily: "'Times New Roman', Georgia, serif",
            margin: '0 auto',
            color: '#000',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}>
            {/* Watermark Background */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'center',
              justifyContent: 'center',
              opacity: 0.06,
              pointerEvents: 'none',
              overflow: 'hidden',
              zIndex: 0
            }}>
              {[...Array(80)].map((_, i) => (
                <span key={i} style={{ fontSize: '0.7rem', margin: '2px 8px', whiteSpace: 'nowrap', color: '#18664d', fontWeight: 'bold' }}>
                  SWAMI VIVEKANANDA UNIVERSITY
                </span>
              ))}
            </div>

            {/* Inner Border */}
            <div style={{
              border: '2px solid #18664d',
              height: '100%',
              position: 'relative',
              padding: '12px 20px',
              boxSizing: 'border-box',
              zIndex: 1
            }}>

              {/* Serial Number - Top Left */}
              <EditableField
                style={{
                  position: 'absolute',
                  top: '8px',
                  left: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: '#000'
                }}
              >
                SL. NO. : REG/2023/3378
              </EditableField>

              {/* Header Section */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '12px', marginTop: '15px' }}>
                {/* University Logo */}
                <img
                  src="/logo.png"
                  alt="SVU Logo"
                  style={{
                    width: '70px',
                    height: '70px',
                    objectFit: 'contain',
                    flexShrink: 0
                  }}
                />

                {/* University Name */}
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <EditableField
                    style={{
                      margin: 0,
                      color: '#18664d',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      letterSpacing: '2px',
                      fontFamily: "'Times New Roman', serif",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    SWAMI VIVEKANANDA UNIVERSITY
                  </EditableField>

                  {/* Green Strip */}
                  <EditableField
                    style={{
                      background: '#18664d',
                      color: '#fff',
                      padding: '4px 10px',
                      margin: '6px auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      letterSpacing: '1px',
                      width: '60%'
                    }}
                  >
                    EXCELLENCE.   INNOVATION.   ENTREPRENEURSHIP
                  </EditableField>

                  {/* Website */}
                  <EditableField
                    style={{
                      color: '#18664d',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      marginTop: '2px'
                    }}
                  >
                    www.swamivivekanandauniversity.ac.in
                  </EditableField>
                </div>
              </div>

              {/* Photo Box */}
              <div style={{
                position: 'absolute',
                top: '70px',
                right: '25px',
                width: '90px',
                height: '110px',
                border: '1px solid #333',
                background: '#fff',
                padding: '2px'
              }}>
                {(() => {
                  const photoUrl = s.image_path || s.photo_path || s.photo_url || s.photo || s.image || s.profile_image || s.profileImage;
                  const getFullUrl = (p) => {
                    if (!p) return '';
                    if (p.startsWith('http')) return p;
                    let cleanPath = p.startsWith('/') ? p.substring(1) : p;
                    return `${API_LINK}/${cleanPath}`;
                  };
                  return photoUrl ? (
                    <>
                      {!photoLoaded && (
                        <div style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          background: 'linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)',
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 1.5s infinite',
                          zIndex: 2, borderRadius: '2px'
                        }} />
                      )}
                      <img
                        src={getFullUrl(photoUrl)}
                        alt="Student"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: photoLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
                        onLoad={() => setPhotoLoaded(true)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          setPhotoLoaded(true);
                        }}
                      />
                    </>
                  ) : null;
                })()}
                <div style={{
                  width: '100%', height: '100%', background: '#e8e8e8',
                  display: (s.image_path || s.photo_path || s.photo_url || s.photo || s.image || s.profile_image || s.profileImage) ? 'none' : 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#888'
                }}>
                  Photo
                </div>
              </div>

              {/* Registration Certificate Ribbon */}
              <div style={{ textAlign: 'center', margin: '20px 0 15px 0' }}>
                <div style={{
                  background: '#18664d',
                  color: '#fff',
                  padding: '8px 60px',
                  display: 'inline-block',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  fontStyle: 'italic',
                  position: 'relative',
                  fontFamily: "'Times New Roman', serif"
                }}>
                  {/* Left ribbon fold */}
                  <div style={{
                    position: 'absolute',
                    left: '-15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 0,
                    height: 0,
                    borderTop: '20px solid transparent',
                    borderBottom: '20px solid transparent',
                    borderRight: '15px solid #18664d'
                  }}></div>
                  {/* Right ribbon fold */}
                  <div style={{
                    position: 'absolute',
                    right: '-15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 0,
                    height: 0,
                    borderTop: '20px solid transparent',
                    borderBottom: '20px solid transparent',
                    borderLeft: '15px solid #18664d'
                  }}></div>
                  <EditableField>Registration Certificate</EditableField>
                </div>
              </div>

              {/* Form Content */}
              <div style={{ padding: '0 30px', fontSize: '1rem', lineHeight: '2', maxWidth: '700px' }}>
                {/* Sri/Smt Line */}
                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '8px' }}>
                  <EditableField style={{ fontWeight: 'bold', marginRight: '5px' }}>Sri / Smt.</EditableField>
                  <EditableField
                    style={{
                      borderBottom: '1px dotted #000',
                      flex: 1,
                      minHeight: '1.2em',
                      fontWeight: 'bold',
                      paddingLeft: '5px'
                    }}
                  >{s.name || s.student_name || ''}</EditableField>
                </div>

                {/* S/D of Line */}
                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '12px' }}>
                  <EditableField style={{ fontWeight: 'bold', marginRight: '5px' }}>S/D of</EditableField>
                  <EditableField
                    style={{
                      borderBottom: '1px dotted #000',
                      flex: 1,
                      minHeight: '1.2em',
                      fontWeight: 'bold',
                      paddingLeft: '5px'
                    }}
                  >{s.father_name || s.guardian_name || s.parent_name || ''}</EditableField>
                </div>

                {/* Registration Statement */}
                <EditableField
                  isBlock
                  style={{
                    textAlign: 'center',
                    margin: '12px 0',
                    fontWeight: 'bold',
                    fontSize: '1.05rem'
                  }}
                >
                  has been registered as a student of this University
                </EditableField>

                {/* Registration Number Line */}
                <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <EditableField>His/Her Registration Number is</EditableField>
                  <EditableField
                    style={{
                      borderBottom: '1px dotted #000',
                      minWidth: '180px',
                      minHeight: '1.2em',
                      margin: '0 5px',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}
                  >{s.student_id || ''}</EditableField>
                  <EditableField>of</EditableField>
                  <EditableField
                    style={{
                      borderBottom: '1px dotted #000',
                      minWidth: '80px',
                      minHeight: '1.2em',
                      margin: '0 5px',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}
                  >{s.admission_year || ''}</EditableField>
                </div>
              </div>

              {/* Registrar Signature */}
              <div style={{
                position: 'absolute',
                bottom: '50px',
                right: '40px',
                textAlign: 'center'
              }}>
                <EditableField
                  style={{
                    fontFamily: "'Brush Script MT', cursive",
                    fontSize: '1.4rem',
                    marginBottom: '2px'
                  }}
                >
                  Registrar
                </EditableField>
                <EditableField style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Registrar</EditableField>
              </div>

              {/* Footer Address Bar */}
              <EditableField
                style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  background: '#18664d',
                  color: '#fff',
                  textAlign: 'center',
                  padding: '5px 10px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold'
                }}
              >
                Bara Kanthalia, Barrackpore, P.O.: Sewli Telini Para, Kolkata - 700121, Dist. - North 24 Parganas, State : West Bengal
              </EditableField>

            </div>
          </div>
        );
      case 'library':
        return (
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* FRONT SIDE */}
            <div>
              <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#888', marginBottom: '6px', fontWeight: 600 }}>▸ Front Side</p>
              <div style={{
                width: '450px',
                height: '280px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                border: '1px solid #238548',
                padding: '0.8rem 1rem',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: '#238548', opacity: 0.1, borderRadius: '50%' }}></div>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
                  <img src="/logo.png" alt="SVU Logo" style={{ width: '30px', height: '30px', objectFit: 'contain', borderRadius: '50%', background: '#fff', padding: '1px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }} />
                  <div>
                    <h3 style={{ margin: 0, color: '#238548', fontSize: '0.95rem' }}><span style={{ marginRight: '4px' }}>📚</span>Library Card</h3>
                    <p style={{ margin: 0, fontSize: '0.65rem', color: '#666' }}>Swami Vivekananda University</p>
                  </div>
                </div>
                {/* Details + Photo */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1 }}>
                  <div style={{ fontSize: '0.82rem', lineHeight: '1.65', display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '0 8px', alignContent: 'start', textAlign: 'left', width: '100%' }}>
                    <span style={{ fontWeight: 'bold' }}>Student ID</span><span>:</span><span>{s.student_id ? `${s.student_id}` : 'SVU-LIB-2025'}</span>
                    <span style={{ fontWeight: 'bold' }}>Name</span><span>:</span><span>{s.name || s.student_name || 'John Doe'}</span>
                    <span style={{ fontWeight: 'bold' }}>Dept</span><span>:</span><span style={{ fontSize: '0.75rem' }}>{s.dept || 'Computer Science'}</span>
                    <span style={{ fontWeight: 'bold' }}>Contact</span><span>:</span><span>{s.phone_number || s.phone || s.contact || '+91-XXXXXXXXXX'}</span>
                    <span style={{ fontWeight: 'bold' }}>Session</span><span>:</span><span>{s.session || s.academic_year || '2025-2026'}</span>
                    <span style={{ fontWeight: 'bold' }}>DOB</span><span>:</span><span>{s.dob ? formatDate(s.dob) : '01-01-2000'}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: '100%', marginLeft: '8px' }}>
                    <div style={{ padding: '3px', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', flexShrink: 0 }}>
                      {(() => {
                        const photoUrl = s.image_path || s.photo_path || s.photo_url || s.photo || s.image || s.profile_image || s.profileImage;

                        // Debug log
                        if (photoUrl) {
                          console.log('Attempting to load photo:', photoUrl);
                        }

                        // Helper to generate URL
                        const getFullUrl = (path, prefix = '') => {
                          if (!path) return null;
                          if (path.startsWith('http') || path.startsWith('blob:')) return path;

                          // Clean up path: remove double slashes, ensure single leading slash for API_LINK check
                          let cleanPath = path.startsWith('/') ? path.substring(1) : path;

                          // If prefix provided, prepend it
                          if (cleanPath.startsWith('/')) {
                            cleanPath = cleanPath.substring(1);
                          }
                          // Remove additional leading slashes if any
                          while (cleanPath.startsWith('/')) {
                            cleanPath = cleanPath.substring(1);
                          }

                          if (prefix && !cleanPath.startsWith(prefix) && !cleanPath.includes('uploads')) {
                            // Only prepend prefix if it's not already there and path doesn't look absolute-ish
                            cleanPath = `${prefix}${cleanPath}`;
                          }

                          const finalUrl = `${API_LINK}/${cleanPath}`;
                          console.log('🖼️ Photo Debug:', { original: path, clean: cleanPath, final: finalUrl });
                          return finalUrl;
                        };

                        return photoUrl ? (
                          <>
                            {!photoLoaded && (
                              <div style={{
                                position: 'absolute', top: 0, left: 0, width: '60px', height: '70px',
                                background: 'linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)',
                                backgroundSize: '200% 100%',
                                animation: 'shimmer 1.5s infinite',
                                zIndex: 2, borderRadius: '2px'
                              }} />
                            )}
                            <img
                              src={getFullUrl(photoUrl)}
                              alt="Student"
                              style={{ width: '60px', height: '70px', objectFit: 'cover', borderRadius: '2px', display: 'block', opacity: photoLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
                              onLoad={() => setPhotoLoaded(true)}
                              onError={(e) => {
                                // Retry Logic
                                const currentSrc = e.target.src;
                                const filename = photoUrl.split('/').pop(); // Extract filename just in case

                                // 1. Try /uploads/
                                const tryUploads = `${API_LINK}/uploads/${filename}`;
                                // 2. Try /uploads/profiles/
                                const tryProfiles = `${API_LINK}/uploads/profiles/${filename}`;
                                // 3. Try /svu/uploads/profile_images/ (Legacy)
                                const tryLegacy = `${API_LINK}/svu/uploads/profile_images/${filename}`;

                                if (currentSrc !== tryUploads && !e.target.dataset.triedUploads) {
                                  e.target.src = tryUploads;
                                  e.target.dataset.triedUploads = true;
                                } else if (currentSrc !== tryProfiles && !e.target.dataset.triedProfiles) {
                                  e.target.src = tryProfiles;
                                  e.target.dataset.triedProfiles = true;
                                } else if (currentSrc !== tryLegacy && !e.target.dataset.triedLegacy) {
                                  e.target.src = tryLegacy;
                                  e.target.dataset.triedLegacy = true;
                                } else {
                                  // Give up
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                  setPhotoLoaded(true);
                                }
                              }}
                            />
                            <div style={{ display: 'none', width: '60px', height: '70px', background: '#f0f0f0', borderRadius: '2px' }}></div>
                          </>
                        ) : (
                          <div style={{ width: '60px', height: '70px', background: '#f0f0f0', borderRadius: '2px' }}></div>
                        );
                      })()}
                    </div>
                    <div style={{ marginTop: 'auto', textAlign: 'center', paddingTop: '10px' }}>
                      <img src="/signature.png" alt="" style={{ height: '20px', display: 'block', margin: '0 auto', opacity: 0 }} /> {/* Placeholder for actual signature image if available */}
                      <div style={{ width: '90px', borderTop: '1px solid #333', marginTop: '2px' }}></div>
                      <span style={{ fontSize: '0.55rem', fontWeight: '700', color: '#333', display: 'block', marginTop: '2px' }}>Authorized Signature</span>
                    </div>
                  </div>
                </div>
                {/* Card Number - Embossed Style */}
                <div style={{
                  textAlign: 'center',
                  fontSize: '1.05rem',
                  fontFamily: "'Courier New', monospace",
                  fontWeight: 900,
                  letterSpacing: '3px',
                  color: '#999',
                  textShadow: '1px 1px 0px rgba(255,255,255,0.9), -1px -1px 0px rgba(0,0,0,0.2)',
                  WebkitTextStroke: '0.5px #aaa',
                  marginTop: '4px',
                  paddingTop: '4px',
                  borderTop: '1px dashed #ddd'
                }}>
                  {s.card_number || s.temp_card_number || '4521  7834  0012  5689'}
                </div>
              </div>
            </div>

            {/* BACK SIDE */}
            <div>
              <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#888', marginBottom: '6px', fontWeight: 600 }}>▸ Back Side</p>
              <div style={{
                width: '450px',
                height: '280px',
                background: 'linear-gradient(135deg, #f8fdf9 0%, #e8f5e9 100%)',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                border: '1px solid #238548',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Magnetic Stripe */}
                <div style={{ width: '100%', height: '35px', background: 'linear-gradient(180deg, #1a1a2e 0%, #333 50%, #1a1a2e 100%)', marginTop: '15px' }}></div>

                {/* Content Area */}
                <div style={{ padding: '12px 1.5rem', flex: 1, position: 'relative' }}>
                  {/* Watermark Logo */}
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', opacity: 0.04 }}>
                    <img src="/logo.png" alt="" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
                  </div>

                  <div style={{ padding: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' }}>
                      <h4 style={{ margin: 0, color: '#1a5f7a', fontSize: '0.7rem', textTransform: 'uppercase', borderBottom: '1px solid #1a5f7a' }}>Rules & Regulations</h4>
                    </div>
                    <ol style={{ fontSize: '0.45rem', paddingLeft: '12px', margin: '2px 0', lineHeight: '1.2', color: '#333', textAlign: 'left' }}>
                      <li style={{ marginBottom: '1px' }}>Students must have valid Library Card for availing library services. Library Card is not transferable.</li>
                      <li style={{ marginBottom: '1px' }}>Overdue charge for beyond the stipulated time period may be imposed according to the library rules.</li>
                      <li style={{ marginBottom: '1px' }}>Books are issued against requisition slip, duly filled up by the students themselves between 11.30 a.m. and 1.00 p.m & they have to borrow it on the same day between 2 p.m. to 4.00 p.m.</li>
                      <li style={{ marginBottom: '1px' }}>Users can also renew the books again after the completion of charging period, subject to not being requested from some other user.</li>
                      <li style={{ marginBottom: '1px' }}>Use of scissor, blade, cutter, gum, adhesive, paste, etc. is prohibited inside the Library.</li>
                      <li style={{ marginBottom: '1px' }}>Students are bound to replace the book in case of loss or damage.</li>
                      <li>Loss of membership card must be reported to the librarian immediately. The member may have to bear a charge for preparing duplicate card. If the lost card is subsequently found, it must be returned to the librarian without delay.</li>
                    </ol>

                    <div style={{ marginTop: '4px', textAlign: 'center', borderTop: '1px dashed #ccc', paddingTop: '2px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '1px' }}>
                        <span style={{ fontSize: '0.6rem', color: '#e91e63' }}>📞</span>
                        <span style={{ fontSize: '0.5rem', fontWeight: 'bold', color: '#1a5f7a' }}>Library Help Desk</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '0.45rem', color: '#666' }}>Email: library@svuniversity.ac.in | Phone: +91-XXXX-XXXXXX</p>
                    </div>

                    <div style={{ marginTop: '2px', textAlign: 'center' }}>
                      <svg ref={(el) => canvasRef.current[index] = el} className="barcode"
                        jsbarcode-format="CODE128"
                        jsbarcode-value={s.card_number || s.temp_card_number || ("SVU" + s.student_id)}
                        jsbarcode-textmargin="0"
                        jsbarcode-fontoptions="bold"
                        jsbarcode-height="25"
                        jsbarcode-width="1"
                        jsbarcode-displayvalue="false"
                        style={{ width: '100%', maxWidth: '180px', height: '25px' }}
                      ></svg>
                      <p style={{ fontSize: '0.55rem', margin: '0', letterSpacing: '2px', fontFamily: 'monospace', color: '#333' }}>
                        {s.card_number || s.temp_card_number || '4521 7834 0012 5689'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div >
        );
      case 'id_card':
        return (
          <div style={{
            width: '350px',
            height: '550px',
            background: '#fff',
            borderRadius: '15px',
            overflow: 'hidden',
            boxShadow: '0 0 20px rgba(0,0,0,0.2)',
            fontFamily: 'Arial, sans-serif',
            position: 'relative',
            margin: '0 auto',
            border: '1px solid #ddd'
          }}>
            {/* Header Section */}
            <div style={{
              background: '#521818', // Deep Maroon
              padding: '15px 10px 40px 10px',
              textAlign: 'center',
              color: '#fff',
              borderBottomLeftRadius: '50% 20px',
              borderBottomRightRadius: '50% 20px',
              position: 'relative',
              clipPath: 'polygon(0 0, 100% 0, 100% 85%, 50% 100%, 0 85%)', // Triangle bottom shape
              height: '180px'
            }}>
              {/* Logo and University Name */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/logo.png" alt="Logo" style={{ width: '40px', height: '40px', marginRight: '10px', background: '#fff', borderRadius: '50%', padding: '2px' }} />
                <div>
                  <h2 style={{ margin: 0, fontSize: '0.9rem', fontFamily: "'Times New Roman', serif", textTransform: 'uppercase', letterSpacing: '0.5px', color: '#FFD700' }}>
                    SWAMI VIVEKANANDA UNIVERSITY
                  </h2>
                </div>
              </div>

              {/* Sub-header Pill */}
              <div style={{ background: '#e0f7fa', color: '#006064', display: 'inline-block', padding: '2px 10px', borderRadius: '10px', fontSize: '0.6rem', fontWeight: 'bold', marginBottom: '5px' }}>
                Excellence • Innovation • Entrepreneurship
              </div>

              {/* Address */}
              <p style={{ margin: '0 0 0 0', fontSize: '0.75rem', opacity: 0.9 }}>
                Barasat - Barrackpore Road, Telinipara,
              </p>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.75rem', opacity: 0.9 }}>
                Bara Kanthalia, Kolkata - 700 121
              </p>

              {/* Card Title */}
              <h3 style={{ margin: 0, fontSize: '0.85rem', textTransform: 'uppercase', textDecoration: 'underline', color: '#FFD700', fontFamily: "'Times New Roman', serif" }}>
                STUDENT IDENTITY CARD
              </h3>
            </div>

            {/* Photo Section */}
            {/* Photo Section */}
            <div style={{
              position: 'absolute',
              top: '160px',
              left: '120px', // Fixed position (350px width - 110px image) / 2 = 120px
              // transform: 'translateX(-50%)', // Removed to fix html2canvas clipping issue
              width: '110px',
              height: '130px',
              zIndex: 10, // Ensure it sits above background elements
              background: '#f0f0f0',
              borderRadius: '10px',
              border: '4px solid #fff',
              boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {(() => {
                const photoUrl = s.image_path || s.photo_path || s.photo_url || s.photo || s.image || s.profile_image || s.profileImage;

                // Helper to generate URL (same as above)
                const getFullUrl = (path, prefix = '') => {
                  if (!path) return null;
                  if (path.startsWith('http') || path.startsWith('blob:')) return path;

                  // Clean up path: remove double slashes, ensure single leading slash for API_LINK check
                  let cleanPath = path.startsWith('/') ? path.substring(1) : path;

                  // If prefix provided, prepend it
                  if (cleanPath.startsWith('/')) {
                    cleanPath = cleanPath.substring(1);
                  }
                  // Remove additional leading slashes if any
                  while (cleanPath.startsWith('/')) {
                    cleanPath = cleanPath.substring(1);
                  }

                  if (prefix && !cleanPath.startsWith(prefix) && !cleanPath.includes('uploads')) {
                    // Only prepend prefix if it's not already there and path doesn't look absolute-ish
                    cleanPath = `${prefix}${cleanPath}`;
                  }

                  const finalUrl = `${API_LINK}/${cleanPath}`;
                  console.log('🖼️ ID Card Photo:', { original: path, clean: cleanPath, final: finalUrl });
                  return finalUrl;
                };

                return photoUrl ? (
                  <>
                    {!photoLoaded && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        background: 'linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                        zIndex: 2, borderRadius: '10px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <span style={{ fontSize: '1.5rem', opacity: 0.4 }}>📷</span>
                      </div>
                    )}
                    <img
                      src={getFullUrl(photoUrl)}
                      alt="Student"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: photoLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
                      onLoad={() => setPhotoLoaded(true)}
                      onError={(e) => {
                        // Retry logic similar to other card
                        const currentSrc = e.target.src;
                        const filename = photoUrl.split('/').pop();

                        const tryUploads = `${API_LINK}/uploads/${filename}`;
                        const tryProfiles = `${API_LINK}/uploads/profiles/${filename}`;

                        if (currentSrc !== tryUploads && !e.target.dataset.triedUploads) {
                          e.target.src = tryUploads;
                          e.target.dataset.triedUploads = true;
                        } else if (currentSrc !== tryProfiles && !e.target.dataset.triedProfiles) {
                          e.target.src = tryProfiles;
                          e.target.dataset.triedProfiles = true;
                        } else {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                          setPhotoLoaded(true);
                        }
                      }}
                    />
                  </>
                ) : (
                  <span style={{ fontSize: '3rem', color: '#ccc' }}>👤</span>
                );
              })()}
              {/* Fallback for error or missing image (only if logic above fails to render img or img hidden) */}
              <span style={{ fontSize: '3rem', color: '#ccc', display: s.image_path ? 'none' : 'block', position: 'absolute', zIndex: -1 }}>👤</span>
            </div>

            {/* Content Section */}
            <div style={{ marginTop: '120px', padding: '0 20px', textAlign: 'center' }}>
              {/* Name */}
              <h2 style={{ margin: '0 0 15px 0', color: '#1a237e', fontSize: '1.4rem', fontFamily: 'Arial, sans-serif', fontWeight: 'bold' }}>
                {s.name || 'Student Name'}
              </h2>

              {/* Details Grid */}
              <div style={{ textAlign: 'left', fontSize: '0.8rem', lineHeight: '1.6', color: '#333' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 10px 1fr', marginBottom: '5px' }}>
                  <div style={{ fontWeight: 'bold', color: '#555' }}>Student ID</div>
                  <div>:</div>
                  <div style={{ fontWeight: 'bold' }}>{s.student_id || 'SVU/BBIO/2023/047'}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 10px 1fr', marginBottom: '5px' }}>
                  <div style={{ fontWeight: 'bold', color: '#555' }}>Department</div>
                  <div>:</div>
                  <div style={{ fontWeight: 'bold' }}>{s.dept || 'B.Sc.(H) in Biotechnology'}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 10px 1fr', marginBottom: '5px' }}>
                  <div style={{ fontWeight: 'bold', color: '#555' }}>Session</div>
                  <div>:</div>
                  <div style={{ fontWeight: 'bold' }}>{s.session || `${s.year || '2023'}-${parseInt(s.year || '2023') + 4}`}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 10px 1fr', marginBottom: '5px' }}>
                  <div style={{ fontWeight: 'bold', color: '#555' }}>Blood Group</div>
                  <div>:</div>
                  <div style={{ fontWeight: 'bold' }}>{s.blood_group || 'A+'}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 10px 1fr', marginBottom: '5px' }}>
                  <div style={{ fontWeight: 'bold', color: '#555' }}>Emergency Contact</div>
                  <div>:</div>
                  <div style={{ fontWeight: 'bold' }}>{s.phone_number || s.phone || '9876543210'}</div>
                </div>
              </div>
            </div>
            {/* Footer */}
            <div style={{
              position: 'absolute',
              bottom: '15px',
              right: '20px',
              textAlign: 'center'
            }}>
              <div style={{ height: '30px', marginBottom: '2px' }}>
                {/* Signature Placeholder */}
                <img src="/signature.png" alt="" style={{ height: '100%', opacity: 0.6 }} />
              </div>
              <div style={{ fontSize: '0.7rem', fontWeight: 'bold', borderTop: '1px solid #333', paddingTop: '2px' }}>
                Authorised Signatory
              </div>
            </div>
          </div>
        );
      case 'laundry_card':
        return (
          <div style={{
            width: '600px',
            background: '#fff',
            padding: '2rem',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            borderTop: '5px solid #6f42c1',
            margin: '0 auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <div>
                <h2 style={{ margin: 0, color: '#6f42c1' }}>SVU Laundry Services</h2>
                <p style={{ margin: 0, color: '#666' }}>Monthly Subscription Card</p>
              </div>
              <div style={{ fontSize: '3rem' }}>🧺</div>
            </div>

            <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', background: '#f8f9fa', padding: '1rem', borderRadius: '8px' }}>
              <div>
                <strong>Name:</strong> John Doe
              </div>
              <div>
                <strong>Room No:</strong> 204 (Hostel B)
              </div>
              <div>
                <strong>Month:</strong> January 2026
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
              {[...Array(20)].map((_, i) => (
                <div key={i} style={{
                  border: '1px solid #ddd',
                  padding: '0.5rem',
                  textAlign: 'center',
                  borderRadius: '4px',
                  background: i < 5 ? '#e9ecef' : '#fff'
                }}>
                  <div style={{ fontSize: '0.7rem', color: '#666' }}>Token</div>
                  <div style={{ fontWeight: 'bold' }}>{i + 1}</div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'mess_card':
        return (
          <div style={{
            width: '700px',
            background: '#fff8e1',
            border: '2px solid #ffc107',
            padding: '2rem',
            boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
            fontFamily: 'Courier New, monospace',
            margin: '0 auto'
          }}>
            <div style={{ textAlign: 'center', borderBottom: '2px dashed #ffc107', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: '#d35400' }}>SVU MESS HALL</h2>
              <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>STUDENT MESS CARD - JAN 2026</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div>Student: <strong>John Doe</strong></div>
              <div>ID: <strong>SVU/HO/2025/112</strong></div>
              <div>Type: <strong>Veg / Non-Veg</strong></div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d35400' }}>
              <thead>
                <tr style={{ background: '#ffd54f', color: '#3e2723' }}>
                  <th style={{ padding: '0.5rem', border: '1px solid #d35400' }}>Date</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #d35400' }}>Breakfast</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #d35400' }}>Lunch</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #d35400' }}>Dinner</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map(date => (
                  <tr key={date} style={{ background: '#fff' }}>
                    <td style={{ padding: '0.5rem', border: '1px solid #d35400', textAlign: 'center' }}>{date} Jan</td>
                    <td style={{ padding: '0.5rem', border: '1px solid #d35400', textAlign: 'center' }}>⬜</td>
                    <td style={{ padding: '0.5rem', border: '1px solid #d35400', textAlign: 'center' }}>⬜</td>
                    <td style={{ padding: '0.5rem', border: '1px solid #d35400', textAlign: 'center' }}>⬜</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'admit':
        return (
          <>
            <div ref={admitCardRef} style={{
              width: '700px',
              aspectRatio: '1/1.414',
              margin: '0 auto',
              background: '#f5f5f0',
              padding: '30px 40px',
              // boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              position: 'relative',
              fontFamily: "'Times New Roman', Times, serif",
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}>
              {/* Watermark Background */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexWrap: 'wrap',
                alignContent: 'flex-start',
                opacity: 0.08,
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: 0
              }}>
                {[...Array(30)].map((_, i) => (
                  <div key={i} style={{
                    width: '100%',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#000',
                    letterSpacing: '0.1em',
                    whiteSpace: 'nowrap',
                    padding: '8px 0'
                  }}>
                    SWAMI VIVEKANANDA UNIVERSITY &nbsp;&nbsp; SWAMI VIVEKANANDA UNIVERSITY &nbsp;&nbsp; SWAMI VIVEKANANDA UNIVERSITY
                  </div>
                ))}
              </div>

              {/* Content Container */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Document Number - Top Left */}
                <EditableField style={{
                  fontSize: '0.85rem',
                  color: '#000',
                  fontWeight: '400',
                  marginBottom: '15px',
                  display: 'block',
                  textAlign: 'left',
                  width: '30%'
                }}>
                  No.: {admitCardNo || 'Generating...'}
                </EditableField>

                {/* University Header */}
                <div style={{ textAlign: 'center', marginBottom: '10px', marginTop: '-40px' }}>
                  {/* University Logo */}
                  <div style={{
                    width: '70px',
                    height: '70px',
                    margin: '0 auto 8px',
                    borderRadius: '50%',
                    overflow: 'hidden'
                  }}>
                    <img src="/logo.png" alt="University Logo" style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }} />
                  </div>

                  <EditableField
                    isBlock
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      margin: '0',
                      color: '#000',
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase'
                    }}
                  >
                    SWAMI VIVEKANANDA UNIVERSITY
                  </EditableField>

                  <div style={{
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    margin: '5px 0',
                    color: '#000',
                    textDecoration: 'underline'
                  }}>
                    ADMIT
                  </div>

                  <EditableField
                    isBlock
                    style={{
                      fontSize: '0.8rem',
                      margin: '5px 0 0 0',
                      color: '#000',
                      fontWeight: '400'
                    }}
                  >
                    EVEN Semester Examinations (Under CBCS/ NEP/ Non-NEP Curriculum), 2023
                  </EditableField>
                </div>

                {/* Student Details Section */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '15px',
                  marginTop: '15px'
                }}>
                  {/* Left: Student Details */}
                  <div style={{ flex: 1 }}>
                    {/* Name */}
                    <div style={{ display: 'flex', marginBottom: '3px', fontSize: '0.85rem' }}>
                      <span style={{ width: '140px', color: '#000', textAlign: 'start' }}>Name of the Candidate</span>
                      <span style={{ marginRight: '8px' }}>:</span>
                      <EditableField style={{ color: '#000', fontWeight: '500' }}>
                        {s.name || s.student_name || ''}
                      </EditableField>
                    </div>

                    {/* Semester */}
                    <div style={{ display: 'flex', marginBottom: '3px', fontSize: '0.85rem' }}>
                      <span style={{ width: '140px', color: '#000', textAlign: 'start' }}>Semester</span>
                      <span style={{ marginRight: '8px' }}>:</span>
                      <EditableField style={{ color: '#000', fontWeight: '500' }}>
                        {s.year ? (() => { const sem = (s.year - 2000) * 2; const suffix = sem === 2 ? 'nd' : sem === 4 ? 'th' : sem === 6 ? 'th' : 'th'; return `${sem}${suffix}`; })() : ''}
                      </EditableField>
                    </div>

                    {/* Department */}
                    <div style={{ display: 'flex', marginBottom: '3px', fontSize: '0.85rem' }}>
                      <span style={{ width: '140px', color: '#000', textAlign: 'start' }}>Department</span>
                      <span style={{ marginRight: '8px' }}>:</span>
                      <EditableField style={{ color: '#000', fontWeight: '500' }}>
                        {s.dept || ''}
                      </EditableField>
                    </div>

                    {/* Registration Number */}
                    <div style={{ display: 'flex', marginBottom: '3px', fontSize: '0.85rem' }}>
                      <span style={{ width: '140px', color: '#000', textAlign: 'start' }}>Registration Number</span>
                      <span style={{ marginRight: '8px' }}>:</span>
                      <EditableField style={{ color: '#000', fontWeight: '500' }}>
                        {s.reg_number || ''}
                      </EditableField>
                    </div>

                    {/* Roll Number */}
                    <div style={{ display: 'flex', fontSize: '0.85rem' }}>
                      <span style={{ width: '140px', color: '#000', textAlign: 'start' }}>Roll Number</span>
                      <span style={{ marginRight: '8px' }}>:</span>
                      <EditableField style={{ color: '#000', fontWeight: '500' }}>
                        {s.roll_number || s.roll_no || ''}
                      </EditableField>
                    </div>
                  </div>

                  {/* Right: Photo & Exam Seal */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                    {/* Student Photo */}
                    <div style={{
                      width: '80px',
                      height: '100px',
                      border: '1px solid #333',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      {s.image_path ? (
                        <img src={`${API_LINK}${s.image_path}`} alt="Student" style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
                      ) : (
                        <div style={{ color: '#999', fontSize: '0.65rem', textAlign: 'center' }}>Photo</div>
                      )}
                    </div>

                  </div>
                </div>

                {/* Schedule Table */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    textAlign: 'center',
                    background: '#fff',
                    padding: '6px',
                    margin: '0 0 0 0',
                    border: '1px solid #000',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    color: '#000',
                    letterSpacing: '0.02em'
                  }}>
                    SCHEDULE FOR SEMESTER EXAMINATIONS**
                  </div>

                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    border: '1px solid #000',
                    background: '#fff',
                    fontSize: '0.75rem'
                  }}>
                    <thead>
                      <tr style={{ background: '#f8f8f8' }}>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', color: '#000', width: '90px', whiteSpace: 'nowrap' }}>
                          DATE
                        </th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', color: '#000' }}>
                          NAME OF THE PAPER
                        </th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', color: '#000', width: '65px' }}>
                          PAPER<br />CODE
                        </th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', color: '#000', width: '70px' }}>
                          SEMESTER
                        </th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', color: '#000', width: '75px' }}>
                          TIME
                        </th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', color: '#000', width: '100px', fontSize: '0.65rem' }}>
                          Full Signature of<br />Invigilator with<br />Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(examSubjects.length > 0 ? examSubjects : [
                        { exam_date: '', subject_name: '', subject_code: '', semester: '', exam_time: '' },
                        { exam_date: '', subject_name: '', subject_code: '', semester: '', exam_time: '' },
                        { exam_date: '', subject_name: '', subject_code: '', semester: '', exam_time: '' },
                        { exam_date: '', subject_name: '', subject_code: '', semester: '', exam_time: '' },
                        { exam_date: '', subject_name: '', subject_code: '', semester: '', exam_time: '' }
                      ]).map((exam, index) => (
                        <tr key={index}>
                          <td style={{ border: '1px solid #000', padding: '12px 6px', textAlign: 'center', color: '#000', whiteSpace: 'nowrap' }}>
                            {exam.exam_date ? new Date(exam.exam_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '12px 6px', textAlign: 'center', color: '#000' }}>
                            {exam.subject_name || ''}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '12px 6px', textAlign: 'center', color: '#000' }}>
                            {exam.subject_code || ''}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '12px 6px', textAlign: 'center', color: '#000' }}>
                            {exam.semester || ''}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '12px 6px', textAlign: 'center', color: '#000', whiteSpace: 'pre-line', fontSize: '0.7rem' }}>
                            {exam.exam_time || ''}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '12px 6px', background: '#fafafa' }}></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  marginTop: '20px'
                }}>
                  {/* Subject to changes note */}
                  <div>
                    <p style={{ fontSize: '0.8rem', color: '#000', fontWeight: '400', margin: 0, fontStyle: 'italic' }}>
                      **Subject to unavoidable changes
                    </p>
                  </div>

                  {/* Controller signature */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontFamily: "'Brush Script MT', cursive",
                      fontSize: '1.2rem',
                      marginBottom: '2px',
                      color: '#333'
                    }}>
                      Signature
                    </div>
                    <div style={{ width: '150px', borderBottom: '1px solid #000', marginBottom: '3px' }}></div>
                    <EditableField style={{ fontWeight: 'bold', fontSize: '0.75rem', color: '#000' }}>
                      Controller of Examinations
                    </EditableField>
                  </div>
                </div>

              </div>
            </div>

            {/* Download & Assign Button */}
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <button
                onClick={handleDownloadAndAssignAdmitCard}
                style={{
                  padding: '10px 30px',
                  background: '#238548',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                📥 Download & Assign Admit Card
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  // Early returns removed to keep search always visible
  // if (loading && cards.length === 0) {... }
  // if (error) {... }

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-student-cards" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Student Card Management Portal 🆔
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Assign and manage student cards by department, year, and batch (Library, ID, Admit, etc.)
        </p>
      </div>

      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        {/* Row 1: Search + Card Type */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem',
          marginBottom: '1.2rem',
          paddingBottom: '1.2rem',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {/* Search Bar */}
          <div style={{ flex: '1 1 auto', maxWidth: isMobile ? '100%' : '400px', minWidth: '250px' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>🔍 Search Student</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Name, Student ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  width: '100%',
                  padding: '0.7rem 2.5rem 0.7rem 0.8rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#238548';
                  e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '1.1rem' }}>🔍</span>
            </div>
          </div>

          {/* Card Type */}
          <div style={{ minWidth: '160px' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>🃏 Card Type</label>
            <select
              value={filterCardType}
              onChange={(e) => setFilterCardType(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {cardTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div style={{ minWidth: '140px' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>📊 Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        {/* Row 2: Department + Batch + Year + Semester + Actions */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          flexWrap: 'wrap',
          gap: '0.8rem'
        }}>
          {/* Department */}
          <div style={{ minWidth: '180px', flex: '1 1 auto' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>🏛️ Department</label>
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Departments</option>
              {uniqueDepartments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          {/* Batch */}
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>📋 Batch</label>
            <select
              value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Batches</option>
              {uniqueBatches.map(batch => (
                <option key={batch} value={batch}>{batch}</option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div style={{ minWidth: '120px' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>📅 Year</label>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Years</option>
              <option value="1">1st Year</option>
              <option value="2">2nd Year</option>
              <option value="3">3rd Year</option>
              <option value="4">4th Year</option>
            </select>
          </div>

          {/* Semester */}
          <div style={{ minWidth: '120px' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>📖 Semester</label>
            <select
              value={filterSemester}
              onChange={(e) => setFilterSemester(e.target.value)}
              style={{
                width: '100%',
                padding: '0.7rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Semesters</option>
              {semesterOptions.map(sem => (
                <option key={sem} value={sem}>{sem}</option>
              ))}
            </select>
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            style={{
              padding: '0.7rem 1.5rem',
              background: 'linear-gradient(135deg, #238548 0%, #2ea05a 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(35, 133, 72, 0.3)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 4px 12px rgba(35, 133, 72, 0.4)'; }}
            onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 2px 8px rgba(35, 133, 72, 0.3)'; }}
          >
            🔍 Search Students
          </button>

          {/* Clear Filters Button */}
          {(searchTerm || filterDepartment || filterBatch || filterYear || filterSemester) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterDepartment('');
                setFilterBatch('');
                setFilterYear('');
                setFilterSemester('');
              }}
              style={{
                padding: '0.7rem 1.2rem',
                background: '#fff',
                color: '#dc3545',
                border: '2px solid #dc3545',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => { e.target.style.background = '#dc3545'; e.target.style.color = 'white'; }}
              onMouseLeave={(e) => { e.target.style.background = '#fff'; e.target.style.color = '#dc3545'; }}
            >
              ✕ Clear
            </button>
          )}
        </div>

        {/* Selected Card Format Preview */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '2px dashed #97d6ac',
          marginBottom: '2rem',
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8fff9'
        }}>
          <h3 style={{ alignSelf: 'center', margin: '0 0 1.5rem 0', color: '#238548', fontSize: '1.2rem', fontWeight: 600 }}>
            {cardTypes.find(c => c.value === filterCardType)?.label} Format
          </h3>
          <div style={{ overflowX: 'auto', maxWidth: '100%', padding: '1rem' }}>
            {renderCardTemplate(filterCardType)}
          </div>
          {/* Print Size Info */}
          <p style={{
            margin: '1rem 0 0 0',
            fontSize: '0.85rem',
            color: '#666',
            textAlign: 'center',
            background: '#f0fdf4',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            border: '1px solid #dcfce7'
          }}>
            🖨️ <strong>Print Size:</strong>{' '}
            {filterCardType === 'registration' ? 'A5 (210mm × 148mm / 8.27" × 5.83")' :
              filterCardType === 'library' ? 'CR80 (85.6mm × 53.98mm / 3.375" × 2.125") — Standard Card Size' :
                filterCardType === 'id_card' ? '54mm × 86mm (2.13" × 3.39") — Vertical ID Card' :
                  filterCardType === 'laundry_card' ? 'CR80 (85.6mm × 53.98mm / 3.375" × 2.125") — Standard Card Size' :
                    filterCardType === 'mess_card' ? 'CR80 (85.6mm × 53.98mm / 3.375" × 2.125") — Standard Card Size' :
                      filterCardType === 'admit' ? 'A4 (210mm × 297mm / 8.27" × 11.69")' :
                        'Standard Print Size'}
          </p>
        </div>

        {/* Summary Stats Cards */}
        {matchedStudents.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: '1rem',
            paddingTop: '1.2rem',
            borderTop: '1px solid #ecf0f1'
          }}>
            {/* Total Students */}
            <div style={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              borderRadius: '10px',
              padding: '1rem 1.2rem',
              textAlign: 'center',
              border: '1px solid #90caf9'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>👥</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1565c0' }}>{matchedStudents.length}</div>
              <div style={{ fontSize: '0.75rem', color: '#1976d2', fontWeight: 600 }}>Total Students</div>
            </div>
            {/* Cards Issued */}
            <div style={{
              background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
              borderRadius: '10px',
              padding: '1rem 1.2rem',
              textAlign: 'center',
              border: '1px solid #a5d6a7'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>✅</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#2e7d32' }}>
                {matchedStudents.filter(s => {
                  const sid = s.student_id || s.id;
                  return cards.some(c => c.student_id === sid && c.card_type === filterCardType);
                }).length}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#388e3c', fontWeight: 600 }}>Cards Issued</div>
            </div>
            {/* Pending */}
            <div style={{
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              borderRadius: '10px',
              padding: '1rem 1.2rem',
              textAlign: 'center',
              border: '1px solid #ffcc80'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>⏳</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#e65100' }}>
                {matchedStudents.filter(s => {
                  const sid = s.student_id || s.id;
                  return !cards.some(c => c.student_id === sid && c.card_type === filterCardType);
                }).length}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#ef6c00', fontWeight: 600 }}>Pending</div>
            </div>
            {/* Selected */}
            <div style={{
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
              borderRadius: '10px',
              padding: '1rem 1.2rem',
              textAlign: 'center',
              border: '1px solid #ce93d8'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>☑️</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#7b1fa2' }}>{selectedStudents.length}</div>
              <div style={{ fontSize: '0.75rem', color: '#8e24aa', fontWeight: 600 }}>Selected</div>
            </div>
          </div>
        )}
        {!matchedStudents.length && (
          <div style={{ paddingTop: '1rem', borderTop: '1px solid #ecf0f1', fontSize: '0.9rem', color: '#666' }}>
            {loading ? 'Loading cards...' :
              error ? 'Error loading cards' :
                `Select filters and click "🔍 Search Students" to find students`}
          </div>
        )}
      </div>

      {/* Matched Student Details Section (New Tab Style) */}
      {matchedStudents.length > 0 && (
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '2px solid #238548',
          marginBottom: '2rem',
          overflow: 'hidden',
          boxShadow: '0 10px 25px rgba(35, 133, 72, 0.15)',
          display: 'flex',
          flexDirection: 'column'
        }}>



          {/* Bulk Actions Bar */}
          {selectedStudents.length > 0 && (
            <div style={{
              background: '#e8f5e9',
              padding: '0.75rem 1.5rem',
              borderBottom: '1px solid #c8e6c9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              animation: 'fadeIn 0.3s ease'
            }}>
              <div style={{ fontWeight: '600', color: '#2e7d32' }}>
                {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={handleBulkGenerate}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#2e7d32',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  📇 Generate {selectedStudents.length} Cards
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          <div style={{ overflowX: 'auto', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '950px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr style={{ background: '#238548', color: '#ffffff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <th style={{ padding: '15px', textAlign: 'center', width: '50px' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={matchedStudents.length > 0 && selectedStudents.length === matchedStudents.length}
                      style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                    />
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '0.95rem' }}>Name</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '0.95rem' }}>Student ID</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '0.95rem' }}>Department</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '0.95rem' }}>Batch</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '0.95rem' }}>Year</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '0.95rem' }}>Semester</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '0.95rem' }}>Roll Number</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '0.95rem' }}>Card Status</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '0.95rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {matchedStudents.map((student, index) => {
                  // Format year helper
                  const getYearLabel = (yearCode) => {
                    const yearMap = { 2001: '1st Year', 2002: '2nd Year', 2003: '3rd Year', 2004: '4th Year' };
                    if (yearCode >= 2000) return yearMap[yearCode] || yearCode;
                    if (yearCode >= 1 && yearCode <= 4) return `${yearCode === 1 ? '1st' : yearCode === 2 ? '2nd' : yearCode === 3 ? '3rd' : '4th'} Year`;
                    return yearCode || '-';
                  };

                  const studentId = student.id || student.student_id;
                  const isSelected = selectedStudents.includes(studentId);
                  // Check if student has an active card: cross-reference against the cards list from backend
                  const hasCard = cards.some(c =>
                    (c.student_id === student.student_id || c.student_id === student.id) &&
                    (c.card_status === 'active' || c.card_type === filterCardType)
                  );

                  return (
                    <tr key={index} style={{
                      borderBottom: '1px solid #eee',
                      backgroundColor: isSelected ? '#e8f5e9' : (index % 2 === 0 ? '#ffffff' : '#f8f9fa'),
                      transition: 'background-color 0.2s',
                      textAlign: 'start'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#c8e6c9' : '#f0fff4'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#e8f5e9' : (index % 2 === 0 ? '#ffffff' : '#f8f9fa')}
                    >
                      <td style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectStudent(studentId)}
                          style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                        />
                      </td>
                      <td style={{ padding: '12px 15px', color: '#333', fontWeight: '500' }}>{student.name}</td>
                      <td style={{ padding: '12px 15px', color: '#555' }}>
                        <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                          {student.student_id}
                        </span>
                      </td>
                      <td style={{ padding: '12px 15px', color: '#555' }}>{student.dept || '-'}</td>
                      <td style={{ padding: '12px 15px', color: '#555' }}>
                        <span style={{ background: '#fff3cd', color: '#856404', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem' }}>
                          {student.batch_name || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 15px', color: '#555' }}>{getYearLabel(student.year)}</td>
                      <td style={{ padding: '12px 15px', color: '#555' }}>{student.year ? Math.max(1, (student.year - 2000) * 2 - 1) : '-'}</td>
                      <td style={{ padding: '12px 15px', color: '#555' }}>{student.roll_number || '-'}</td>
                      {/* Card Status */}
                      <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                        {hasCard ? (
                          <span style={{
                            background: '#d4edda',
                            color: '#155724',
                            padding: '5px 12px',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            🟢 Issued
                          </span>
                        ) : (
                          <span style={{
                            background: '#f0f0f0',
                            color: '#666',
                            padding: '5px 12px',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            ⚪ Not Issued
                          </span>
                        )}
                      </td>
                      {/* Actions */}
                      <td style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button
                            onClick={() => handleViewCard(student)}
                            style={{
                              cursor: 'pointer',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid #3498db',
                              background: 'transparent',
                              color: '#3498db',
                              fontWeight: '500',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => { e.target.style.background = '#3498db'; e.target.style.color = 'white'; }}
                            onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#3498db'; }}
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={() => handleViewCard(student)}
                            style={{
                              cursor: 'pointer',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: hasCard ? '1px solid #e67e22' : '1px solid #238548',
                              background: 'transparent',
                              color: hasCard ? '#e67e22' : '#238548',
                              fontWeight: '500',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => { e.target.style.background = hasCard ? '#e67e22' : '#238548'; e.target.style.color = 'white'; }}
                            onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = hasCard ? '#e67e22' : '#238548'; }}
                          >
                            {hasCard ? '🔄 Re-Assign' : '🆔 Assign'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Student Cards</p>
          <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
          <button
            onClick={() => fetchCards(currentPage, searchTerm, filterCardType, filterStatus)}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}





      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                🗑️
              </div>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#dc2626'
              }}>
                Delete Card
              </h3>
              <p style={{
                margin: '0 0 1rem 0',
                color: '#6b7280',
                lineHeight: 1.6
              }}>
                Are you sure you want to delete <strong style={{ color: '#374151' }}>{selectedCard.card_name}</strong> for {selectedCard.student_name}?
              </p>
              <p style={{
                margin: 0,
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                This action cannot be undone and will permanently remove the card data.
              </p>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem 2rem 2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedCard(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCard}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  '🗑️ Delete Card'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shimmer animation keyframe */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* PREVIEW CARD MODAL */}
      {showPreview && previewStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '2rem',
            position: 'relative',
            boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
          }}>
            <button
              onClick={() => { setShowPreview(false); setPreviewStudent(null); }}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#f1f5f9',
                border: 'none',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                fontSize: '1.2rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b'
              }}
            >
              ✕
            </button>

            <h2 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Card Preview</h2>
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', fontSize: '0.9rem', color: '#666' }}>
              <span>Student: <strong>{previewStudent.name || previewStudent.student_name}</strong></span>
              <span>Type: <strong>{cardTypes.find(c => c.value === filterCardType)?.label || 'Card'}</strong></span>
            </div>

            <div style={{
              padding: '2rem',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px dashed #cbd5e1',
              display: 'flex',
              justifyContent: 'center',
              minHeight: '400px',
              alignItems: 'center'
            }}>
              <div id="card-preview-target">
                {renderCardTemplate(filterCardType, previewStudent)}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
              {/* Conditional Buttons based on Card Status */}
              {filterCardType === 'library' && previewStudent.card_status === 'active' ? (
                <>
                  <button
                    onClick={() => window.open(`${API_LINK}${previewStudent.file_path}`, '_blank')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      background: '#0ea5e9', // Sky blue
                      color: 'white',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span>📥</span> Download Assigned Card
                  </button>
                  <button
                    onClick={() => handleAutoAssignCard(previewStudent)}
                    disabled={!photoLoaded}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: '1px solid #eab308', // Yellow border
                      background: !photoLoaded ? '#f5f5f5' : 'white',
                      color: !photoLoaded ? '#999' : '#b45309',
                      borderRadius: '8px',
                      cursor: !photoLoaded ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      opacity: !photoLoaded ? 0.6 : 1,
                      transition: 'all 0.3s'
                    }}
                  >
                    {!photoLoaded ? (
                      <><span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: '0.9rem' }}>⏳</span> Loading Photo...</>
                    ) : (
                      <><span>🔄</span> Re-Assign Card</>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleAutoAssignCard(previewStudent)}
                  disabled={!photoLoaded}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: !photoLoaded ? '#94d3a2' : '#238548',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: !photoLoaded ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: !photoLoaded ? 0.7 : 1,
                    transition: 'all 0.3s'
                  }}
                >
                  {!photoLoaded ? (
                    <><span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: '0.9rem' }}>⏳</span> Loading Photo...</>
                  ) : (
                    <><span>🆔</span> Assign Card</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminStudentCardManage;